import React, { useState, useEffect } from 'react';
import { TerminalLoginForm } from './TerminalLoginForm';

import {
  CenterStack,
  Box,
  Cluster,
  ButtonWithAction,
  withWindowSize,
  util,
  Modal,
  Text,
  constants,
  Detail,
} from '@thecb/components';
import WelcomeHeader from '../../components/welcome-header';
import { useOktaAuth } from '@okta/okta-react';
import AlertBar from '../../components/alert-bar';
import { useDispatch, useSelector } from 'react-redux';
import { can } from '../../utils/helpers';
import {
  CLOSE_SESSION_TRANSFER_MODAL,
  OPEN_SESSION_TRANSFER_MODAL,
  sessionTransferModal,
  submitTerminalLoginForm,
} from './TerminalLogin.state';
import { useNavigate } from 'react-router-dom';

const SUBMIT_ACTIONS = {
  SESSION_NEW: {
    key: 'SESSION_NEW',
    label: 'Start New Session',
  },
  SESSION_CONTINUE: {
    key: 'SESSION_CONTINUE',
    label: 'Continue Session',
  },
  SESSION_TRANSFER: {
    key: 'SESSION_TRANSFER',
    label: 'Transfer Session',
  },
};

const TerminalLoginFormBody = ({
  forms,
  actions,
  showErrors,
  submitting,
  handleSubmit,
  locations,
  terminals,
  submitBtnText,
  setSelectedTerminalSession,
}) => (
  <Box
    key="item2"
    padding="0"
    width="576px"
    borderRadius="0 0 8px 8px"
    boxShadow="0px 2px 14px 0px rgb(246, 246, 249),
    0px 3px 8px 0px rgb(202, 206, 216)"
  >
    <Box padding="24px">
      <TerminalLoginForm
        {...forms.terminalLoginForm}
        {...actions.forms.terminalLoginForm}
        showErrors={showErrors}
        locations={locations}
        terminals={terminals}
        setSelectedTerminalSession={setSelectedTerminalSession}
      />
      <Box padding="1rem 0 0 0">
        <Cluster justify="center" align="center">
          <ButtonWithAction
            text={submitBtnText}
            isLoading={submitting}
            variant="primary"
            action={handleSubmit}
            extraStyles={'width: 100%; margin: 0px;'}
            dataQa="Submit"
            textExtraStyles="font-size: 1rem;"
          />
        </Cluster>
      </Box>
    </Box>
  </Box>
);

const TerminalLogin = ({
  forms,
  actions,
  submitting,
  fetchLocations,
  locations,
  terminals,
  alertBars,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errors, handleErrors] = useState(false);
  const [selectedTerminalSession, setSelectedTerminalSession] = useState(null);
  const { isSessionTransferModalOpen } = useSelector(
    (state) => state.terminalLogin,
  );
  const { oktaAuth } = useOktaAuth();
  const handleFetchLocations = async () => {
    try {
      let exists = await oktaAuth.session.exists();
      let isAuthenticated = await oktaAuth.isAuthenticated();
      if (exists && isAuthenticated) {
        fetchLocations({ navigate });
      }
    } catch (err) {
      console.warn(
        `%cWarning: Error checking Okta session before fetching locations: ${err}`,
        `background-color: ${consoleWarningStyle}`,
      );
    }
  };
  useEffect(() => {
    handleFetchLocations();
  }, []);

  const { currentAbilities } = useSelector((state) => state.shared);
  const { cashier: loggedInCashier } = useSelector((state) => state.login);

  const getSubmitAction = () => {
    const hasActiveSession =
      selectedTerminalSession?.terminal_session.cashier['active?'];
    if (hasActiveSession) {
      const isSelectedTerminalSessionTheCurrentUsers =
        selectedTerminalSession.terminal_session.cashier?.email ===
        loggedInCashier?.email;
      if (isSelectedTerminalSessionTheCurrentUsers) {
        return SUBMIT_ACTIONS.SESSION_CONTINUE.key;
      } else {
        return can('transfer_session', 'TerminalSession', currentAbilities)
          ? SUBMIT_ACTIONS.SESSION_TRANSFER.key
          : SUBMIT_ACTIONS.SESSION_CONTINUE.key;
        /* 
        This Continue Session path intentionally leads to an error in certain conditions -> 
        a non-admin can receive the error that another cashier is operating this session (with no option to transfer it to them)
        */
      }
    } else {
      return SUBMIT_ACTIONS.SESSION_NEW.key;
    }
  };

  const submitAction = getSubmitAction();

  const handleSubmit = util.general.generateClickHandler(
    forms.terminalLoginForm,
    () => handleErrors(true),
    () =>
      dispatch(
        submitTerminalLoginForm({
          isSessionTransfer:
            submitAction === SUBMIT_ACTIONS.SESSION_TRANSFER.key ? true : false,
          email: selectedTerminalSession.terminal_session.cashier.email,
          id: selectedTerminalSession.id,
          terminal_session_id:
            selectedTerminalSession.terminal_session.terminal_session_id,
          navigate,
        }),
      ),
  );

  const modalHeaderText = (
    <Detail
      variant="large"
      weight={constants.fontWeights.FONT_WEIGHT_BOLD}
      color={constants.colors.WHITE}
    >
      Session Transfer
    </Detail>
  );

  return (
    <CenterStack
      content={
        <Box padding="32px 0 64px">
          {alertBars.terminalLoginAlertBar.alerts.length > 0 && (
            <Box padding="16px 0">
              <AlertBar
                {...alertBars.terminalLoginAlertBar}
                {...actions.alertBars.terminalLoginAlertBar}
              />
            </Box>
          )}
          <Box
            padding="0"
            borderRadius="8px"
            boxShadow="0px 1px 2px 0px rgba(41, 42, 51, 0.1), 0px 2px 6px 0px rgba(41, 42, 51, 0.2), 0px 1px 0px 0px rgba(41, 42, 51, 0.1) inset"
          >
            <WelcomeHeader actionText="Please select your location" />
            <TerminalLoginFormBody
              submitBtnText={SUBMIT_ACTIONS[submitAction].label}
              setSelectedTerminalSession={setSelectedTerminalSession}
              forms={forms}
              actions={actions}
              showErrors={errors}
              submitting={submitting}
              handleSubmit={(e) => {
                submitAction === SUBMIT_ACTIONS.SESSION_TRANSFER.key
                  ? dispatch(sessionTransferModal(OPEN_SESSION_TRANSFER_MODAL))
                  : handleSubmit(e);
              }}
              locations={locations}
              terminals={terminals}
            />
          </Box>
          {selectedTerminalSession && (
            <Modal
              modalHeaderBg={constants.colors.ERROR_COLOR}
              modalHeaderText={modalHeaderText}
              noBorder
              defaultWrapper={false}
              hideModal={() => {
                dispatch(sessionTransferModal(CLOSE_SESSION_TRANSFER_MODAL));
              }}
              continueAction={handleSubmit}
              continueButtonText={SUBMIT_ACTIONS.SESSION_TRANSFER.label}
              modalOpen={isSessionTransferModalOpen}
              modalBodyText={
                <Box>
                  <Text>
                    {`This terminal has an open session that belongs to ${selectedTerminalSession.terminal_session.cashier.full_name}. `}
                  </Text>
                  <Text>
                    Continuing will log this user out and transfer this session
                    to you.
                  </Text>
                </Box>
              }
            />
          )}
        </Box>
      }
    />
  );
};

export default withWindowSize(TerminalLogin);
